import { z } from "zod";
import {
	monedaSchema,
	propiedadEstadoSchema,
	tipoPropiedadSchema,
	vistaPropiedadSchema,
	nivelAmobladoSchema,
	estadoConstruccionSchema,
	optionalNumber,
	requiredPositiveNumber,
	optionalDateString,
	requiredDateString,
	optionalString,
	requiredString,
	timestampFieldsSchema,
	createPaginatedSchema,
	listParamsSchema,
	TIPOS_PROPIEDAD,
	VISTAS_PROPIEDAD,
	NIVELES_AMOBLADO,
	ESTADOS_CONSTRUCCION,
} from "@schemas/common";
import { formatDateISO } from "@/utils/formatters";

// Re-export constants and types for convenience
export {
	TIPOS_PROPIEDAD,
	VISTAS_PROPIEDAD,
	NIVELES_AMOBLADO,
	ESTADOS_CONSTRUCCION,
};
export type {
	Moneda,
	EstadoConstruccion,
	VistaPropiedad,
	NivelAmoblado,
	TipoPropiedad,
	PropiedadEstado,
	TipoBono,
} from "@schemas/common";

// =============================================================================
// BASE PROPERTY SCHEMA (shared validation rules)
// =============================================================================

const propertyBaseSchema = z.object({
	nombre: requiredString.max(200),
	precio_compra: requiredPositiveNumber,
	moneda: monedaSchema,
	fecha_adquisicion: requiredDateString.refine(
		(val) => {
			const date = new Date(val);
			const maxDate = new Date();
			maxDate.setFullYear(maxDate.getFullYear() + 3);
			return date <= maxDate;
		},
		{ message: "fecha_adquisicion_futura" },
	),
	estado: propiedadEstadoSchema.default("activa"),
	descripcion: optionalString.transform((val) => val?.slice(0, 500)),
	fecha_entrega: optionalDateString,
	metadatos: z.record(z.unknown()).optional(),
	bono: optionalNumber,
	tipo_bono: optionalString,
});

// Validation refinements (Dates, Bono consistency)
const propertyRefinements = (
	data: z.infer<typeof propertyBaseSchema>,
	ctx: z.RefinementCtx,
) => {
	// 1. Validate fecha_entrega >= fecha_adquisicion
	if (data.fecha_entrega && data.fecha_adquisicion) {
		if (new Date(data.fecha_entrega) < new Date(data.fecha_adquisicion)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["fecha_entrega"],
				message: "fecha_entrega_anterior",
			});
		}
	}
};

// =============================================================================
// CREATE SCHEMA (for POST /propiedad)
// =============================================================================

export const propertyCreateSchema =
	propertyBaseSchema.superRefine(propertyRefinements);

export type PropertyCreate = z.infer<typeof propertyCreateSchema>;

// =============================================================================
// UPDATE SCHEMA (for PATCH /propiedad/{id})
// =============================================================================

export const propertyUpdateSchema = propertyBaseSchema.partial();

export type PropertyUpdate = z.infer<typeof propertyUpdateSchema>;

// =============================================================================
// BASE PROPERTY DETAIL SCHEMA
// =============================================================================

const propertyDetailBaseSchema = z.object({
	tipo: tipoPropiedadSchema
		.nullable()
		.optional()
		.transform((val) => (val === null ? undefined : val)),
	estado: estadoConstruccionSchema
		.nullable()
		.optional()
		.transform((val) => (val === null ? undefined : val)),
	direccion: optionalString,
	departamento: optionalString.pipe(z.string().max(100).optional()),
	provincia: optionalString.pipe(z.string().max(100).optional()),
	distrito: optionalString.pipe(z.string().max(100).optional()),
	urbanizacion: optionalString.pipe(z.string().max(100).optional()),
	latitud: optionalNumber.refine(
		(val) => val === undefined || (val >= -90 && val <= 90),
		{ message: "invalid_latitude" },
	),
	longitud: optionalNumber.refine(
		(val) => val === undefined || (val >= -180 && val <= 180),
		{ message: "invalid_longitude" },
	),
	antiguedad: optionalNumber.refine((val) => val === undefined || val >= 0, {
		message: "non_negative",
	}),
	nro_dormitorios: optionalNumber.refine(
		(val) => val === undefined || val >= 0,
		{ message: "non_negative" },
	),
	nro_banos: optionalNumber.refine((val) => val === undefined || val >= 0, {
		message: "non_negative",
	}),
	nro_estacionamientos: optionalNumber.refine(
		(val) => val === undefined || val >= 0,
		{ message: "non_negative" },
	),
	nro_depositos: optionalNumber
		.refine((val) => val === undefined || val >= 0, {
			message: "non_negative",
		})
		.default(0),
	nro_piso: optionalNumber,
	superficie_construida: optionalNumber,
	superficie_total: optionalNumber,
	superficie_techada: optionalNumber,
	vista: vistaPropiedadSchema
		.nullable()
		.optional()
		.transform((val) => (val === null ? undefined : val)),
	amoblado: nivelAmobladoSchema
		.nullable()
		.optional()
		.transform((val) => (val === null ? undefined : val)),
	ascensor: z.boolean().nullable().optional().default(false),
	gas_natural: z.boolean().nullable().optional(),
	es_duplex: z.boolean().nullable().optional(),
	imagen_url: optionalString,
	imagenes_adicionales: z.array(z.string()).optional(),
	zona_indice_valorizacion: optionalNumber,
	zona_demanda_alquiler: optionalString,
	puntaje_caminabilidad: optionalNumber,
	puntaje_transporte: optionalNumber,
	metadatos: z.record(z.unknown()).optional(),
});

export const PROPERTY_DETAIL_FIELDS = Object.keys(
	propertyDetailBaseSchema.shape,
) as readonly string[];

// =============================================================================
// PROPERTY DETAIL CREATE/UPDATE SCHEMAS
// =============================================================================

const propertyDetailRefinement = (
	data: Partial<z.infer<typeof propertyDetailBaseSchema>>,
	ctx: z.RefinementCtx,
) => {
	// 1. Custom validation: Property under construction or in plans cannot have age > 0
	if (
		(data.estado === "en_construccion" || data.estado === "planos") &&
		data.antiguedad &&
		data.antiguedad > 0
	) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			path: ["antiguedad"],
			message: "construction_status_age_mismatch",
		});
	}

	// 2. Validate Surface Areas
	// superficie_techada <= superficie_construida
	if (data.superficie_techada && data.superficie_construida) {
		if (data.superficie_techada > data.superficie_construida) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["superficie_techada"],
				message: "superficie_techada_exceeds_construida",
			});
		}
	}
	// superficie_construida <= superficie_total
	if (data.superficie_construida && data.superficie_total) {
		if (data.superficie_construida > data.superficie_total) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["superficie_construida"],
				message: "superficie_construida_exceeds_total",
			});
		}
	}

	// 3. Validate Apartment Floor
	if (
		data.tipo === "departamento" &&
		data.nro_piso !== undefined &&
		data.nro_piso !== null
	) {
		if (data.nro_piso < 1) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				path: ["nro_piso"],
				message: "departamento_piso_invalido",
			});
		}
	}
};

export const propertyDetailCreateSchema = propertyDetailBaseSchema.superRefine(
	propertyDetailRefinement,
);
export type PropertyDetailCreate = z.infer<typeof propertyDetailCreateSchema>;

export const propertyDetailUpdateSchema = propertyDetailBaseSchema.partial();
export type PropertyDetailUpdate = z.infer<typeof propertyDetailUpdateSchema>;

// =============================================================================
// COMBINED FORM SCHEMA (for react-hook-form)
// =============================================================================

// Form-specific property schema: uses the strict validations
const propertyFormPropertySchema =
	propertyBaseSchema.superRefine(propertyRefinements);

// Form-specific detail schema - strict typing for react-hook-form compatibility
const propertyFormDetailSchema = propertyDetailBaseSchema.superRefine(
	propertyDetailRefinement,
);

export const propertyFormSchema = z.object({
	property: propertyFormPropertySchema,
	detail: propertyFormDetailSchema.optional(),
});

export type PropertyFormData = z.output<typeof propertyFormSchema>;
export type PropertyFormInput = z.input<typeof propertyFormSchema>;

// =============================================================================
// RESPONSE SCHEMAS (from API)
// =============================================================================

export const propertyResponseSchema = propertyBaseSchema
	.merge(timestampFieldsSchema)
	.extend({
		id: z.number(),
		id_hipoteca_activa: z.number().nullable().optional(),
	});

export type PropertyResponse = z.infer<typeof propertyResponseSchema>;

export const propertyDetailResponseSchema = propertyDetailBaseSchema
	.merge(timestampFieldsSchema)
	.extend({
		id: z.number(),
		id_propiedad: z.number(),
	});

export type PropertyDetailResponse = z.infer<
	typeof propertyDetailResponseSchema
>;

// =============================================================================
// PAGINATED & LIST SCHEMAS
// =============================================================================

export const propertyPaginatedSchema = createPaginatedSchema(
	propertyResponseSchema,
);
export type PropertyPaginated = z.infer<typeof propertyPaginatedSchema>;

export const propertyListParamsSchema = listParamsSchema.extend({
	estado: propiedadEstadoSchema.optional(),
});
export type PropertyListParams = z.infer<typeof propertyListParamsSchema>;

// =============================================================================
// DEFAULT VALUES (Single Source of Truth)
// =============================================================================

export const DEFAULT_PROPERTY_FORM_VALUES: PropertyFormInput = {
	property: {
		nombre: "",
		precio_compra: 0,
		moneda: "PEN",
		fecha_adquisicion: formatDateISO(new Date()),
		fecha_entrega: "",
		estado: "activa",
	},
	detail: {
		tipo: undefined,
		estado: "terminado",
		departamento: "Lima",
		provincia: "Lima",
		distrito: "",
		urbanizacion: "",
		direccion: "",
		gas_natural: false,
		amoblado: "no_amoblado",
		nro_piso: 1,
		vista: "exterior",
	},
};
